// /var/www/oszczedzam/client/src/layouts/MainLayout.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Drawer, List, ListItem, 
  ListItemIcon, ListItemText, IconButton, Container,
  Box, Divider, Avatar, Menu, MenuItem, useTheme, alpha, Button
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  AccountBalanceWallet as WalletIcon,
  EventNote as CalendarIcon,
  TrendingUp as BudgetsIcon,
  BarChart as ReportsIcon,
  Flag as GoalsIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Transakcje', icon: <WalletIcon />, path: '/transactions' },
  { text: 'Kalendarz', icon: <CalendarIcon />, path: '/calendar' },
  { text: 'Budżety', icon: <BudgetsIcon />, path: '/budgets' },
  { text: 'Raporty', icon: <ReportsIcon />, path: '/reports' },
  { text: 'Cele', icon: <GoalsIcon />, path: '/goals' },
  { text: 'Ustawienia', icon: <SettingsIcon />, path: '/settings' }
];

function MainLayout({ children }) {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { currentUser, logout, getUserData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  
  React.useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        const data = await getUserData();
        setUserData(data);
      }
    };
    
    fetchUserData();
  }, [currentUser, getUserData]);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Błąd podczas wylogowywania:", error);
    }
  };

  const drawer = (
    <div>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 'bold', 
            color: 'primary.main',
            background: '-webkit-linear-gradient(45deg, #4CAF50 30%, #2E7D32 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          OSZCZĘDZAM
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Zarządzaj swoim budżetem
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40, 
              mr: 2,
              bgcolor: 'primary.main'
            }}
          >
            {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 'medium' }}>
              {userData?.username || 'Użytkownik'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {currentUser?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Divider />
      <List sx={{ pt: 1 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem 
              button 
              key={item.text} 
              onClick={() => {
                navigate(item.path);
                if (mobileOpen) setMobileOpen(false);
              }}
              sx={{
                mb: 0.5,
                mx: 1,
                borderRadius: 1,
                backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                color: isSelected ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.15) : alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              <ListItemIcon sx={{ color: isSelected ? 'primary.main' : 'text.secondary', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{ 
                  fontWeight: isSelected ? 'medium' : 'regular'
                }}
              />
              {isSelected && (
                <Box 
                  sx={{ 
                    width: 4, 
                    height: 24, 
                    borderRadius: 2,
                    backgroundColor: 'primary.main',
                    ml: 1
                  }}
                />
              )}
            </ListItem>
          );
        })}
      </List>
      <Box 
        sx={{ 
          p: 2, 
          mt: 2, 
          mx: 2, 
          borderRadius: 2, 
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          border: '1px dashed',
          borderColor: alpha(theme.palette.primary.main, 0.2)
        }}
      >
        <Typography variant="subtitle2" gutterBottom color="primary">
          Wspieraj swoje finanse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Dodaj pierwszy cel oszczędnościowy i śledź swoje postępy
        </Typography>
        <Button 
          size="small" 
          variant="outlined" 
          color="primary"
          fullWidth
          onClick={() => navigate('/goals')}
        >
          Utwórz cel
        </Button>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 'medium' }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Oszczędzam'}
          </Typography>
          
          <IconButton color="inherit" sx={{ mr: 1 }}>
            <NotificationsIcon />
          </IconButton>
          
          <IconButton color="inherit" onClick={handleProfileMenuOpen}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              {currentUser?.email?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={() => { 
              navigate('/settings'); 
              handleProfileMenuClose();
            }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Profil</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Wyloguj</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, boxShadow: 3 },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: `1px solid ${theme.palette.divider}` },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: 'background.default'
        }}
      >
        <Container maxWidth="lg" sx={{ py: 2, animation: 'fadeIn 0.4s ease-in-out' }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
}

export default MainLayout;